import { AdminResponse } from "entities/auth/api/getAdmins";
import useRolePermission from "entities/auth/hooks/useRolePermission";
import useAdminListQuery from "entities/auth/queries/useAdminListQuery";
import { useUserRoleStore } from "entities/auth/stores/userRoleStore";
import { FC } from "react";
import DeleteConfirmationModal from "shared/components/DeleteConfirmationModal";
import FlexColumn from "shared/components/Layout/components/FlexColumn";
import PageHeading from "shared/components/PageHeading";
import ResetPasswordModal from "shared/components/ResetPasswordModal";
import TableWithPagination from "shared/components/TableWithPagination";
import AdminDetails from "./components/AdminDetails";
import {
  RolesAndPermissionsPageProvider,
  useRolesAndPermissionsPage,
} from "./context";
import { getRolesAndPermissionsColumns } from "./helpers";
import { StyledAlert, StyledButton } from "./styled";

const RolesAndPermissionsContainer = () => (
  <RolesAndPermissionsPageProvider>
    <RolesAndPermissions />
  </RolesAndPermissionsPageProvider>
);

const RolesAndPermissions: FC = () => {
  const { data = [], isLoading } = useAdminListQuery();
  const {
    adminDetailsDrawer,
    isPendingDeleteAdmin,
    deleteAdminModal,
    handleDeleteAdmin,
    resetPasswordModal,
    handleResetPassword,
    isPendingResetPassword,
    isAlertOpen,
    setIsAlertOpen,
    alertSeverity,
    alertMessage,
  } = useRolesAndPermissionsPage();
  const { roles } = useUserRoleStore();

  const writeAccess = useRolePermission("rolesAndPermissions:write");
  const columns = getRolesAndPermissionsColumns({
    adminDetailsDrawer,
    deleteAdminModal,
    resetPasswordModal,
    roles,
    readOnly: !writeAccess.allowed,
  });

  return (
    <FlexColumn>
      <PageHeading>
        {writeAccess.allowed && (
          <StyledButton
            title="Add New User"
            onClick={() => adminDetailsDrawer.openDrawer()}
          />
        )}
      </PageHeading>
      <TableWithPagination<AdminResponse>
        heightTable="100%"
        data={data}
        columns={columns}
        loading={isLoading}
        emptyStateMessage="No admins found"
        hideFooter
      />
      <AdminDetails />
      <DeleteConfirmationModal
        title="Delete user"
        content="Are you sure you want to delete this user?"
        applyButtonTitle="Yes, delete"
        open={deleteAdminModal.isOpen}
        applyButtonLoading={isPendingDeleteAdmin}
        onClose={deleteAdminModal.closeModal}
        handleSuccess={handleDeleteAdmin}
      />
      <ResetPasswordModal
        title="Reset Password Confirmation"
        content={`Are you sure you want to send a password reset link to ${resetPasswordModal.modalDetails?.email}?`}
        applyButtonTitle="Confirm"
        open={resetPasswordModal.isOpen}
        applyButtonLoading={isPendingResetPassword}
        onClose={resetPasswordModal.closeModal}
        handleSuccess={handleResetPassword}
      />

      {isAlertOpen && (
        <StyledAlert
          severity={alertSeverity}
          onClose={() => setIsAlertOpen(false)}
        >
          {alertMessage}
        </StyledAlert>
      )}
    </FlexColumn>
  );
};

export default RolesAndPermissionsContainer;
